import { FC, useEffect, useRef, useState } from "react";
import { styles } from "../stylesHome";
import { Box, Link, Typography } from "@mui/material";
import { IPost } from "@/store/api/postApiData";
import LogoFull from "@/assets/svg/Logo/LogoFull";
import Article from "@/assets/svg/Article";
import CreateLongPostModal from "@/components/UI/modal/CreateLongPostModal/CreateLongPostModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { safeParseHtml } from "@/helpers/safeParseHtml";

interface IPostContentProps {
  post: IPost;
  isImageValid: boolean;
  setIsImageValid: (value: boolean) => void;
}
// const separator = "᠌᠌᠌᠌᠌᠌᠌";
function splitHtmlByLimit(html: string, limit = 500): { before: string; after: string } {
  if (!html) return { before: "", after: "" };
  if (html.length <= limit) return { before: html, after: "" };

  const cutFrom = Math.max(limit, 0);
  const closeTagMatch = html.slice(cutFrom).match(/<\/[^>]+>/);

  if (closeTagMatch) {
    const closeIdx = cutFrom + closeTagMatch.index! + closeTagMatch[0].length;
    return {
      before: html.slice(0, closeIdx),
      after: html.slice(closeIdx),
    };
  }

  return {
    before: html.slice(0, limit),
    after: html.slice(limit),
  };
}
const PostContentLong: FC<IPostContentProps> = ({ post, isImageValid, setIsImageValid }) => {
  const container = document.createElement("div");
  container.innerHTML = post.content;
  const h2El = container.querySelector("h2");

  const titleHtml = h2El?.innerHTML || "";
  if (h2El) h2El.remove();

  const afterH2 = container.innerHTML;
  const [isLongPostModalOpen, setIsLongPostModalOpen] = useState(false);
  const [beforeLor, setBeforeLor] = useState("");
  const topRef = useRef<HTMLDivElement | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    success?: boolean;
    link: { text: string; href: string };
  } | null>(null);

  useEffect(() => {
    const separatorMatch = afterH2.match(/<separator>([\s\S]*?)<\/separator>/i);

    if (post.postType !== "FREE" && separatorMatch) {
      setBeforeLor(separatorMatch[1] ?? "");
    } else {
      const { before } = splitHtmlByLimit(afterH2, Math.floor(post.freeContentRange || 500));
      setBeforeLor(before);
    }
  }, [afterH2, post.postType, post.freeContentRange]);

  const parsedContent = safeParseHtml(beforeLor);
  const parsedTitle = safeParseHtml(titleHtml);

  const visibleSources =
    post.sources?.filter(
      (src) => !src.startsWith("https://wethermarket-prod.nyc3.digitaloceanspaces.com/")
    ) || [];

  return (
    <>
      {isLongPostModalOpen && (
        <CreateLongPostModal
          open={isLongPostModalOpen}
          setIsLongPostModalOpen={setIsLongPostModalOpen}
          isPreview={true}
          editPost={post}
          setToast={setToast}
        />
      )}
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <Box
        sx={styles.postContentLong}
        ref={topRef}
        onClick={() => {
          setIsLongPostModalOpen(true);
        }}
      >
        {/* watermark */}
        {!post.attachments[0] && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              margin: "0px 0 14px 0",
            }}
          >
            <Box
              sx={{
                width: "91px",
                height: "34px",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LogoFull style={{ width: "100%", height: "100%" }} />
            </Box>

            {post?.postType !== "FREE" && (
              <Box
                sx={{
                  width: "102px",
                  height: "34px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Article />
              </Box>
            )}
          </Box>
        )}

        {post.attachments[0] && (
          <Box
            sx={{
              display: isImageValid ? "block" : "none",
              position: "relative",
              marginBottom: "20px",
            }}
          >
            <Box
              sx={{
                display: isImageValid ? "block" : "none",
                position: "relative",
                borderRadius: "14px",
                overflow: "hidden",
              }}
            >
              <img
                src={post.attachments.at(-1)?.url}
                alt="Bitcoin"
                onError={() => setIsImageValid(false)}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "340px",
                  objectFit: "fill",
                  display: "block",
                }}
              />
            </Box>

            {/* watermark */}
            <Box
              sx={{
                position: "absolute",
                bottom: "10px",
                left: "10px",
                display: "flex",
                flexDirection: "row",
                gap: "10px",
              }}
            >
              <Box
                sx={{
                  width: "91px",
                  height: "34px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LogoFull style={{ width: "100%", height: "100%" }} />
              </Box>

              {post?.postType !== "FREE" && (
                <Box
                  sx={{
                    width: "102px",
                    height: "34px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Article />
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Typography
          variant="h2"
          component={"p"}
          fontWeight={500}
          sx={{
            textAlign: "left",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            fontWeight: 600,
            margin: "0 0 16px 0",
          }}
          className="preview-content"
        >
          {parsedTitle}
        </Typography>

        <div className="preview-content preview-content-post">{parsedContent}</div>

        {visibleSources.length > 0 && (
          <Typography variant="h5" component="p" fontWeight={500} lineHeight="1.5rem">
            Source:
            {visibleSources.map((source) => (
              <Link
                key={source}
                href={source}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  wordBreak: "break-all",
                  overflowWrap: "anywhere",
                }}
              >
                {source}
              </Link>
            ))}
          </Typography>
        )}
      </Box>
    </>
  );
};

export default PostContentLong;
